import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import './ActiveTradesPage.css';
import ActiveTradesHeader from './components/ActiveTradesHeader';
import TradeStatusTabs from './components/TradeStatusTabs';
import ShippingGuideBanner from './components/ShippingGuideBanner';
import TradeCard from './components/TradeCard';
import { useGetActiveTradesQuery } from 'store/services/trade/tradeApi';
import { AssetUrls } from 'config/assets';
import MagicButton from 'components/MagicButton/MagicButton';
import { useTraderBalanceSocket } from 'hooks/useTraderBalanceSocket';

export type TradeStatus = 'ready' | 'transit' | 'completed' | 'cancelled';

export interface TradeItem {
  qty: number;
  name: string;
  set: string;
  setCode?: string;
  setIconUrl?: string;
  condition: string;
  language: string;
  image: string;
  pointValue?: number;
  foil?: boolean;
}

export interface Trade {
  id: string;
  tradeNumber: string;
  traderId: string;
  traderName: string;
  traderAvatar: string;
  pointBalance?: number;
  initiated: string;
  initiatedRaw?: string;  // Keep the raw ISO date for ShippingCountdown
  shipped?: string;
  completed?: string;
  packingSlipAvailable?: boolean;
  packingSlipExpiresAt?: string | null;
  cancelled?: string;
  estimatedDelivery?: string;
  status: TradeStatus;
  items: TradeItem[];
  totalValue: number;
  tradeFee?: number;
  speed: number;
  accuracy: number;
  communication: number;
  shippingAddress: {
    name: string;
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  cancelledRaw?: string;
  isRemovedItem?: boolean;
  removedFromTradeNumber?: string;
}

// Format date from ISO string to MM.DD.YYYY
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}.${day}.${year}`;
  } catch {
    return dateString; // Return original string if parsing fails
  }
};

export default function ActiveTradesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TradeStatus>('ready');
  const [retryCount, setRetryCount] = useState(0);
  const [skipQuery, setSkipQuery] = useState(false);
  const hasSetInitialTab = useRef(false);

  // Fetch active trades from API
  const { data: tradesData, isLoading, error, refetch } = useGetActiveTradesQuery(
    { type: 'sending' },
    {
      // Skip the query if we've had too many 401 errors
      skip: skipQuery
    }
  );

  // Subscribe to real-time balance updates for visible traders
  const traderIds = useMemo(
    () => (tradesData?.trades || []).map(t => t.traderId),
    [tradesData?.trades]
  );
  useTraderBalanceSocket({ traderIds, enabled: traderIds.length > 0 });

  // Convert API trades to component format
  const trades: Trade[] = tradesData?.trades.map(trade => ({
    id: trade.id,
    tradeNumber: trade.tradeNumber,
    traderId: trade.traderId.toString(),
    traderName: trade.traderName,
    traderAvatar: trade.traderAvatar || '',
    pointBalance: trade.pointBalance,
    initiated: formatDate(trade.initiated),
    initiatedRaw: trade.initiated,  // Keep raw date for ShippingCountdown
    shipped: trade.shipped ? formatDate(trade.shipped) : undefined,
    completed: trade.completed ? formatDate(trade.completed) : undefined,
    cancelled: trade.cancelled ? formatDate(trade.cancelled) : undefined,
    cancelledRaw: trade.cancelled || undefined,
    estimatedDelivery: trade.estimatedDelivery,
    packingSlipAvailable: trade.packingSlipAvailable,
    packingSlipExpiresAt: trade.packingSlipExpiresAt,
    status: trade.status,
    items: trade.items.map(item => ({
        qty: item.qty,
        name: item.name,
        set: item.set,
        setCode: (item as any).setCode,
        setIconUrl: (item as any).setIconUrl,
        condition: item.condition,
        language: item.language,
        image: item.image,
        pointValue: item.pointValue || 0,  // Backend should provide the actual point value at trade initiation
        foil: item.foil || false,
    })),
    totalValue: trade.totalValue,
    tradeFee: trade.tradeFee,
    speed: 5,  // Rating out of 5 stars
    accuracy: 5,  // Rating out of 5 stars
    communication: 5,  // Rating out of 5 stars
    shippingAddress: trade.shippingAddress ? {
      name: `${trade.shippingAddress.firstName || ''} ${trade.shippingAddress.lastName || ''}`.trim() || 'Not provided',
      street: trade.shippingAddress.address1 || '',
      street2: trade.shippingAddress.address2 || undefined,
      city: trade.shippingAddress.city || '',
      state: trade.shippingAddress.state || '',
      zipCode: trade.shippingAddress.postalCode || '',
      country: trade.shippingAddress.country || '',
    } : {
      name: 'Not provided',
      street: '',
      street2: undefined,
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
    isRemovedItem: (trade as any).isRemovedItem || false,
    removedFromTradeNumber: (trade as any).removedFromTradeNumber,
  })) || [];

  // Only use real API data - no mock data
  const filteredTrades = trades.filter(trade => trade.status === activeTab)
    .sort((a, b) => {
      if (activeTab === 'cancelled') {
        return new Date(b.cancelledRaw || '').getTime() - new Date(a.cancelledRaw || '').getTime();
      }
      return 0;
    });

  const summaryStats = {
    ready: trades.filter(t => t.status === 'ready').length,
    transit: trades.filter(t => t.status === 'transit').length,
    completed: trades.filter(t => t.status === 'completed').length,
    totalPoints: trades.filter(t => t.status === 'transit').reduce((sum, t) => sum + t.totalValue, 0)
  };

  // Auto-select the first tab with content on initial load
  useEffect(() => {
    if (tradesData && !hasSetInitialTab.current) {
      hasSetInitialTab.current = true;
      if (summaryStats.ready === 0 && summaryStats.transit > 0) {
        setActiveTab('transit');
      }
    }
  }, [tradesData, summaryStats.ready, summaryStats.transit]);

  if (isLoading) {
    return (
      <div className="active-trades-page">
        <ActiveTradesHeader stats={{ ready: 0, transit: 0, completed: 0, totalPoints: 0 }} />
        <ShippingGuideBanner />
        <TradeStatusTabs activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="trades-container">
          <div style={{ padding: '20px', textAlign: 'center', color: '#fff' }}>
            Loading trades...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorStatus = (error as any).status;
    const errorMessage = (error as any).data?.message || (error as any).data?.error;

    console.error('Failed to fetch trades:', {
      status: errorStatus,
      message: errorMessage,
      fullError: error
    });

    // Special handling for 401 errors
    if (errorStatus === 401) {
      console.warn('⚠️ 401 error on /sending page - This might be a backend permission issue');

      // Show a user-friendly error message instead of logging out
      return (
        <div className="active-trades-page">
          <ActiveTradesHeader stats={{ ready: 0, transit: 0, completed: 0, totalPoints: 0 }} />
          <ShippingGuideBanner />
          <TradeStatusTabs activeTab={activeTab} onTabChange={setActiveTab} />
          <div className="trades-container">
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: '#fff',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <h3 style={{ color: '#ff9800', marginBottom: '20px' }}>
                Unable to Load Trades
              </h3>
              <p style={{ marginBottom: '20px' }}>
                We're having trouble loading your trades. This sometimes happens with new accounts.
                Your trade has been initiated successfully and will appear here shortly.
              </p>
              <button
                onClick={() => {
                  const newCount = retryCount + 1;
                  setRetryCount(newCount);
                  if (newCount > 2) {
                    setSkipQuery(true);
                  }
                  refetch();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#9c27b0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Try Again
              </button>
              <p style={{ marginTop: '20px', fontSize: '14px', color: '#aaa' }}>
                If this persists, try refreshing the page or logging out and back in.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // For other errors, show a generic error message
  }

  return (
    <div className="active-trades-page">
      <ActiveTradesHeader stats={summaryStats} />
      <ShippingGuideBanner />
      <TradeStatusTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="trades-container">
        {filteredTrades.length > 0 ? (
          filteredTrades.map(trade => (
            <TradeCard key={trade.id} trade={trade} />
          ))
        ) : (
          <div className="sending-empty-state">
            <img
              src={AssetUrls.logos.arevakhachIcon}
              alt="TradeMagic"
              style={{ width: 64, height: 64 }}
            />
            <div className="sending-empty-state-title">
              No outbound trades yet...
            </div>
            <div className="sending-empty-state-message">
              Head to the "Send Cards" page to find packages to send to the community.
            </div>
            <div className="sending-empty-state-button">
              <MagicButton
                label="Send Cards"
                icon={<img src={AssetUrls.icons.misc.sendPackage} alt="" style={{ width: 18, height: 18 }} />}
                color="purple"
                onPress={() => navigate('/sendcards')}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}